import { useState, useEffect } from "react";
import { BarChart2, Building2, TrendingUp, DollarSign, Activity, Eye, MapPin, Calendar, Users } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact, formatDate } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

const STATUS_BADGE = {
  activo: { label: "Activo", cls: "bg-emerald-100 text-emerald-700" },
  completado: { label: "Completado", cls: "bg-blue-100 text-blue-700" },
  pausado: { label: "Pausado", cls: "bg-amber-100 text-amber-700" },
  cancelado: { label: "Cancelado", cls: "bg-red-100 text-red-700" },
};

function StatCard({ title, value, sub, color = "blue", icon: Icon }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
    pink: "bg-pink-50 text-pink-600 border-pink-100",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <div className={`p-2 rounded-lg border ${colors[color]}`}><Icon size={15} /></div>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function Ventas() {
  const [projects, setProjects] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("todos");

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, tRes] = await Promise.all([
          axios.get(`${API}/projects`, { withCredentials: true }),
          axios.get(`${API}/finance/transactions`, { withCredentials: true }),
        ]);
        setProjects(pRes.data || []);
        setTransactions(tRes.data.items || []);
      } catch {
        toast.error("Error al cargar datos de ventas");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // Calculate revenue per project
  const revenueByProject = transactions
    .filter(t => t.type === "ingreso")
    .reduce((acc, t) => {
      acc[t.project_id] = (acc[t.project_id] || 0) + (t.amount_pyg || 0);
      return acc;
    }, {});

  const enrichedProjects = projects.map(p => ({
    ...p,
    revenue: revenueByProject[p._id] || 0,
    billing_pct: p.budget_pyg > 0 ? Math.round(((revenueByProject[p._id] || 0) / p.budget_pyg) * 100) : 0,
    pending: Math.max(0, (p.budget_pyg || 0) - (revenueByProject[p._id] || 0)),
  }));

  const filtered = enrichedProjects.filter(p => filterStatus === "todos" || p.status === filterStatus);

  const totalContractValue = projects.reduce((a, p) => a + (p.budget_pyg || 0), 0);
  const totalBilled = Object.values(revenueByProject).reduce((a, v) => a + v, 0);
  const totalPending = totalContractValue - totalBilled;
  const activeProjects = projects.filter(p => p.status === "activo");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Ventas</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Obras — Vista Comercial</h1>
        <p className="text-sm text-gray-500 mt-0.5">Facturación y estado comercial de cada obra/contrato</p>
      </div>

      {/* Pipeline stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Contratos Activos" value={activeProjects.length} sub="obras en ejecución" color="emerald" icon={Building2} />
        <StatCard title="Valor Contratado" value={formatPYGCompact(totalContractValue)} sub="cartera total" color="blue" icon={DollarSign} />
        <StatCard title="Facturado" value={formatPYGCompact(totalBilled)} sub={`${totalContractValue > 0 ? Math.round(totalBilled/totalContractValue*100) : 0}% del total`} color="pink" icon={TrendingUp} />
        <StatCard title="Por Facturar" value={formatPYGCompact(totalPending)} sub="saldo pendiente" color="amber" icon={Activity} />
      </div>

      {/* Billing progress bar */}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-gray-900">Pipeline de Ventas</p>
          <span className="text-sm font-bold text-pink-600">{totalContractValue > 0 ? Math.round(totalBilled/totalContractValue*100) : 0}% facturado</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3">
          <div
            className="bg-gradient-to-r from-pink-500 to-rose-400 h-3 rounded-full transition-all duration-700"
            style={{ width: `${totalContractValue > 0 ? Math.min(100, Math.round(totalBilled/totalContractValue*100)) : 0}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-400">
          <span>Facturado: {formatPYGCompact(totalBilled)}</span>
          <span>Cartera Total: {formatPYGCompact(totalContractValue)}</span>
        </div>
      </div>

      {/* Status filters */}
      <div className="flex gap-2 flex-wrap">
        {["todos", "activo", "completado", "pausado"].map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-xl border transition-all capitalize ${filterStatus === s ? "bg-[#EC4899] text-white border-[#EC4899]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`}
          >
            {s === "todos" ? "Todas las obras" : STATUS_BADGE[s]?.label}
          </button>
        ))}
      </div>

      {/* Projects as obras */}
      <div className="grid gap-4">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-28 w-full rounded-2xl" />)
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center">
            <Building2 size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">No hay obras para mostrar</p>
          </div>
        ) : (
          filtered.map(project => {
            const badge = STATUS_BADGE[project.status] || { label: project.status, cls: "bg-gray-100 text-gray-600" };
            return (
              <div key={project._id} className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all group" data-testid={`ventas-obra-${project._id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-md">{project.code}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                    </div>
                    <h3 className="text-base font-bold text-gray-900 truncate">{project.name}</h3>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1"><Users size={11} />{project.client}</span>
                      {project.location && <span className="flex items-center gap-1"><MapPin size={11} />{project.location}</span>}
                      {project.start_date && <span className="flex items-center gap-1"><Calendar size={11} />Inicio: {formatDate(project.start_date)}</span>}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400 uppercase tracking-wide">Valor Contrato</p>
                    <p className="text-lg font-bold text-gray-900">{formatPYGCompact(project.budget_pyg)}</p>
                  </div>
                </div>

                {/* Billing progress */}
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1.5 text-xs text-gray-500">
                    <span>Facturado: <span className="font-bold text-pink-600">{formatPYGCompact(project.revenue)}</span></span>
                    <span className="font-bold">{project.billing_pct}%</span>
                    <span>Por facturar: <span className="font-semibold text-amber-600">{formatPYGCompact(project.pending)}</span></span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-pink-500 to-rose-400 h-2 rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, project.billing_pct)}%` }}
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
